import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, MessageSquare, AlertTriangle, CheckCircle2, MinusCircle, Search, RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

function StatusBadge({ status }) {
  if (status === 'sent') {
    return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#56dfb4]/15 text-[#0f766e]"><CheckCircle2 className="w-3 h-3" />sent</span>;
  }
  if (status === 'failed') {
    return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />failed</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"><MinusCircle className="w-3 h-3" />skipped</span>;
}

function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminSmsLogPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get('/admin/sms-log', { params });
      setRows(data.rows || []);
      setCounts(data.counts || { sent: 0, failed: 0, skipped: 0 });
    } catch {
      toast.error('Failed to load SMS log');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const total = counts.sent + counts.failed + counts.skipped;
  const failureRate = total > 0 ? Math.round((counts.failed / total) * 100) : 0;

  return (
    <div data-testid="admin-sms-log-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#56dfb4]" data-testid="back-to-admin-from-sms-log">
          <ArrowLeft className="w-4 h-4" /> Admin dashboard
        </Link>
        <div className="flex items-center gap-2 mt-2">
          <MessageSquare className="w-7 h-7 text-[#b456df]" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>SMS Delivery Log</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Every Twilio send attempt — sent, failed, or skipped — with actionable error codes.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Sent</p>
            <p className="text-2xl font-bold text-[#0f766e] mt-1" data-testid="sms-counts-sent">{counts.sent}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Failed</p>
            <p className="text-2xl font-bold text-red-700 mt-1" data-testid="sms-counts-failed">{counts.failed}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Skipped</p>
            <p className="text-2xl font-bold text-slate-700 mt-1" data-testid="sms-counts-skipped">{counts.skipped}</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-100 ${failureRate >= 10 ? 'bg-red-50 border-red-200' : ''}`}>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Failure rate</p>
            <p className={`text-2xl font-bold mt-1 ${failureRate >= 10 ? 'text-red-700' : 'text-slate-700'}`} data-testid="sms-failure-rate">
              {failureRate}%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5">
          {STATUS_OPTIONS.map(o => (
            <button
              key={o.value || 'all'}
              type="button"
              onClick={() => setStatus(o.value)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                status === o.value ? 'bg-[#b456df] text-white' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`sms-filter-${o.value || 'all'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by email or phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load(); }}
              className="pl-9 rounded-full bg-white"
              data-testid="sms-search-input"
            />
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full gap-1" data-testid="sms-log-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-10 text-center text-slate-500" data-testid="sms-log-empty">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No SMS attempts match this filter.
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sms-log-table">
                <thead>
                  <tr className="border-b border-slate-100 text-xs text-slate-500">
                    <th className="text-left p-3">Time</th>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Code</th>
                    <th className="text-left p-3">Error / Body</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.log_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`sms-log-row-${r.log_id}`}>
                      <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                      <td className="p-3 text-xs">{r.user_email || <span className="text-slate-400">—</span>}</td>
                      <td className="p-3 text-xs font-mono">{r.to_phone_full || r.to_phone_masked}</td>
                      <td className="p-3"><StatusBadge status={r.status} /></td>
                      <td className="p-3 text-xs font-mono text-slate-600">
                        {r.twilio_code ? (
                          <a
                            href={`https://www.twilio.com/docs/api/errors/${r.twilio_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#b456df] hover:underline"
                            data-testid={`sms-log-twilio-code-${r.log_id}`}
                          >
                            {r.twilio_code}
                          </a>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-3 text-xs text-slate-600 max-w-md">
                        {r.error ? (
                          <span className="text-red-700">{r.error}</span>
                        ) : (
                          <span className="text-slate-500 italic">{(r.body || '').slice(0, 120)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
